import React, { useState, useEffect, useMemo } from "react";
import { Table, InputGroup, Form, Row, Col, Badge, Button, ButtonGroup, Spinner } from "react-bootstrap";
import SearchIcon from "@mui/icons-material/Search";
import moment from "moment";
import AnimationLoading from "../../components/Animationloading";
import { LINEAR_GRADIENT_BG, USER_KEY } from "../../const";
import { useLazyQuery } from "@apollo/client";
import { GET_LOT_INVENTORIES, GET_SURVIVAL_LOTS } from "../../apollo/inventory/query";
import EmptyComponent from "../../components/EmptyComponent";
import PaginationHelper from "../../components/PaginationHelper";
import { convertCurrency, covertTimeZone, numberFormat } from "../../helper";
import CurrentDateComponents from "../../components/CurrentDateComponents";
import { MdOutlineDateRange } from "react-icons/md";
import { useTranslation } from "react-i18next";

export default function GroupLotList(props) {
  const { t } = useTranslation();
  const { _limit, _skip, Pagination_helper } = PaginationHelper();
  const { setIsTitle } = props;
  
  // GraphQL Queries
  const [getSurvivalLots, { data: lotsData, loading: loadingLots }] = 
    useLazyQuery(GET_SURVIVAL_LOTS, { fetchPolicy: "cache-and-network" });
  const [getLotInventories, { data: dataLotInventories, loading: loadingLotInventories }] = 
    useLazyQuery(GET_LOT_INVENTORIES, { fetchPolicy: "cache-and-network" });

  // State
  const [searchText, setSearchText] = useState("");
  const [sortBy, setSortBy] = useState("createdAt_DESC");
  const [tagFilter, setTagFilter] = useState("ALL");
  const [selectedLot, setSelectedLot] = useState("ALL");
  
  // State select date
  const [isCurrentModals, setIsCurrentModals] = useState(false);
  const [activeButton, setActiveButton] = useState("");
  const [filter, setFilter] = useState({
    createdAt_gte: '',
    createdAt_lt: '',
  });

  // Sort options
  const sortOptions = [
    { value: "createdAt_DESC", label: t("inv_sort_latest_date") },
    { value: "createdAt_ASC", label: t("inv_sort_oldest_date") }
  ];

  // Tag filter options
  const tagOptions = [
    { value: "ALL", label: t("inv_in_out") },
    { value: "IN", label: t("inv_in") },
    { value: "OUT", label: t("inv_out") }
  ];

  // Date filter options
  const dateFilterOptions = [
    { value: "today", label: t("inv_date_today") },
    { value: "yesterday", label: t("inv_date_yesterday") },
    { value: "thisWeek", label: t("inv_date_thisWeek") },
    { value: "lastWeek", label: t("inv_date_lastWeek") },
    { value: "thisMonth", label: t("inv_date_thisMonth") },
    { value: "lastMonth", label: t("inv_date_lastMonth") },
    { value: "thisYear", label: t("inv_date_thisYear") },
    { value: "lastYear", label: t("inv_date_lastYear") },
    { value: "custom", label: t("inv_date_custom") }
  ];

  // Memoized data
  const lotInventories = useMemo(() => {
    try {
      if (dataLotInventories?.lotInventories) {
        const parsedData = dataLotInventories?.lotInventories;
        
        if (parsedData?.status) {
          props.setIsTitle(`${t("inv_group_lot_prefix")}(${parsedData?.total || 0})`);
          return parsedData || null;
        }
      }
      return null;
    } catch (error) {
      console.error("Error parsing lot inventory data:", error);
      return null;
    }
  }, [dataLotInventories, props]);

  const lots = useMemo(() => {
    try {
      if (lotsData?.survivals?.data) {
        const data = lotsData.survivals.data || [];
        return data;
      }
      return [];
    } catch (error) {
      console.error("Error parsing lot data:", error);
      return [];
    }
  }, [lotsData]);

  const shop = useMemo(() => {
    const userData = localStorage.getItem(USER_KEY);
    const locals = JSON.parse(userData);
    return locals?.data?.shop
  }, [USER_KEY])

  // Effects
  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;
    
    const fetch = async () => {
      try {
        await Promise.all([
          fetchLotInventory(signal),
          fetchLots(signal)
        ]);
      } catch (error) {
        console.log("Error in fetch:", error);
      }
    }
    
    fetch();
    
    return () => {
      controller.abort();
    };
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;
    
    if (signal.aborted) return;
    
    const timer = setTimeout(() => {
      fetchLotInventory(signal);
    }, 100); // Debounce search
    
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [searchText, filter, sortBy, tagFilter, _skip, _limit, selectedLot]);

  // Fetch functions
  async function fetchLots(signal) {
    try {
      if (signal.aborted || loadingLots) return;
      if (!shop?.id) return;
      
      let where = {
        shop: shop.id,
        isDeleted: false
      };
      
      await getSurvivalLots({
        variables: {
          where
        },
        context: { fetchOptions: { signal } },
      });
    } catch (error) {
      console.error("Error fetching lots:", error);
    }
  }
  
  async function fetchLotInventory(signal) {
    try {
      // if (signal.aborted || loadingLotInventories) return;
      if (!shop?.id) return;
      
      let where = {
        shops: [shop?.id],
        // All data default isDeleted: false
      };

      // Apply lot filter
      if (selectedLot && selectedLot !== "ALL") {
        where = { ...where, lot: selectedLot };
      }
      
      // Apply search filter
      if (searchText.trim() !== "") {
        where = { ...where, lotName: searchText.trim() };
      }
      
      // Apply tag filter
      if (tagFilter !== "ALL") {
        where = { ...where, tag: tagFilter };
      }

      // Apply createdAt filter
      if (filter?.createdAt_gte !== "" && filter?.createdAt_lt !== "") {
        where = {
          ...where,
          createdAt_gte: filter?.createdAt_gte,
          createdAt_lt: filter?.createdAt_lt
        };
      }
      
      await getLotInventories({
        variables: {
          limit: _limit,
          skip: _skip - 1,
          orderBy: sortBy,
          where
        },
        context: { fetchOptions: { signal } },
      });
    } catch (error) {
      console.error("Error fetching lots:", error);
    }
  }

  // Render helper functions
  const renderTagBadge = (tag) => {
    if (tag === "IN") {
      return <Badge bg="success">{t("inv_in")}</Badge>;
    } else if (tag === "OUT") {
      return <Badge bg="danger">{t("inv_out")}</Badge>;
    }
    return <Badge bg="secondary">-</Badge>;
  };
  
  // Render helper functions
  const renderTypeBadge = (type) => {
    if (type === "PURCHASE") {
      return <Badge bg="info">{t("inv_badge_purchase")}</Badge>;
    } else if (type === "EXCEL") {
      return <Badge bg="success">{t("inv_badge_excel")}</Badge>;
    }
    return <Badge bg="secondary">{type || "-"}</Badge>;
  };

  return (
    <div className="group-lot-container">
      {/* Filters */}
      <div className="group-lot-filters" 
      // style={{ padding: "1em" }}
      >
        <Row className="mb-3">
          {/* Lot Name filter enter */}
          <Col md={3}>
            <InputGroup>
              <InputGroup.Text style={{ backgroundColor: "#FFFFFF" }}>
                <SearchIcon sx={{ color: "#6B7280" }} />
              </InputGroup.Text>
              <Form.Control
                placeholder={t("inv_ph_search_lot")}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                style={{ borderLeft: "none" }}
              />
            </InputGroup>
          </Col>
          {/* Lot filter select */}
          <Col md={2}>
            <Form.Select
              value={selectedLot}
              onChange={(e) => setSelectedLot(e.target.value)}
              style={{ color: "#6B7280" }}
            >
              <option value="ALL">{t("inv_all_lots")}</option>
              {loadingLots ? (
                <option disabled>{t("inv_loading")}</option>
              ) : (
                lots.map((lot) => (
                  <option key={lot?.id} value={lot?.id}>
                    {lot?.survivalNumber}
                  </option>
                ))
              )}
            </Form.Select>
          </Col>
          {/* Tag filter select */}
          <Col md={2}>
            <Form.Select
              value={tagFilter}
              onChange={(e) => setTagFilter(e.target.value)}
              style={{ color: "#6B7280" }}
            >
              {tagOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Form.Select>
          </Col>
          {/* SortBy filter select */}
          <Col md={2}>
            <Form.Select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              style={{ color: "#6B7280" }}
            >
              {sortOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Form.Select>
          </Col>
          {/* Date filter buttons */}
          <Col md={3}>
            <Form.Group>
              <Button
                variant="outline-secondary"
                onClick={() => setIsCurrentModals(true)}
                style={{ textAlign: "left", marginTop: '-.2em', width: '100%' }}
              >
                {(loadingLots) ? (
                  <Spinner size="sm" />
                ) : (
                  <MdOutlineDateRange style={{ fontSize: 24 }} />
                )}
                &nbsp;  &nbsp;
                {(filter?.createdAt_gte === "" && filter?.createdAt_lt === "") ? (
                  <span style={{ fontSize: 14 }}>{t("pm_search_by_date")}</span>
                ) : (
                  <span style={{ fontSize: 14 }}>
                    {covertTimeZone(filter?.createdAt_gte) +
                      " - " +
                      covertTimeZone(filter?.createdAt_lt)}
                  </span>
                )}
              </Button>
            </Form.Group>
          </Col>
        </Row>
      </div>
      
      {/* Table */}
      <div className="group-lot-table" style={{ padding: "0 1em" }}>
        {lotInventories?.total === 0 || loadingLotInventories ? (
          <>
          {
            loadingLotInventories ? (<AnimationLoading />) : <EmptyComponent title={t("inv_no_lot_data")} />
          }
          </>
        ) : (
          <Table 
            responsive
            style={{
              marginTop: "10px",
              borderCollapse: "collapse",
              width: "100%",
              background: "#fff"
            }}
          >
            <thead
              style={{
                background: LINEAR_GRADIENT_BG,
                borderRadius: "8px 8px 0px 0px",
                color: "#FFFFFF",
              }}
            >
              <tr>
                <th style={{ width: "5%" }}>{t("inv_th_no")}</th>
                <th style={{ width: "15%" }}>{t("inv_th_lot")}</th>
                <th style={{ width: "15%" }}>{t("inv_th_date")}</th>
                <th style={{ width: "10%" }}>{t("inv_th_items_count")}</th>
                <th style={{ width: "10%" }}>{t("inv_th_total_amount")}</th>
                <th style={{ width: "15%" }}>{t("inv_th_total_price")}</th>
                <th style={{ width: "15%" }}>{t("inv_th_tag")}</th>
                <th style={{ width: "10%" }}>{t("inv_th_via")}</th>
                <th style={{ width: "10%" }}>{t("inv_th_by")}</th>
              </tr>
            </thead>
            <tbody>
              {lotInventories?.dataView?.map((lotIn, index) => (
                <tr key={lotIn?._id}>
                  <td>{(_skip - 1) + index + 1}</td>
                  <td>{lotIn?.lotName}</td>
                  <td>{moment(lotIn?.createdAt).format("DD/MM/YYYY HH:mm")}</td>
                  <td>{numberFormat(lotIn?.documentCount || 0)}</td>
                  <td>{numberFormat(lotIn?.totalAmount || 0)}</td>
                  <td>
                    <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                      <span>{numberFormat(lotIn?.totalPrice || 0)}</span>
                    </div>
                  </td>
                  <td>{renderTagBadge(lotIn?.tag)}</td>
                  <td>{renderTypeBadge(lotIn?.type) || "-"}</td>
                  <td>{lotIn?.createdByName || "-"}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
        
        {/* Pagination */}
        <div className="d-flex justify-content-center mt-3 mb-5">
          {Pagination_helper(lotInventories?.total || 0)}
        </div>
      </div>
      {/* //TODO Modal select date lenght */}
      <CurrentDateComponents
        onCloseModal={() => setIsCurrentModals(false)}
        openModal={isCurrentModals}
        setCdFillter={setFilter}
        setActiveButton={setActiveButton}
        activeButton={activeButton}
      />
    </div>
  );
}
